import { DestroyRef, Injectable, OnDestroy, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PartnerDealBriefingVersionResponse } from '@interfaces/partners/responses/partner-deal-briefing.response';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { PartnersHttpService } from '@services/http/partners/partners-http.service';
import { FilePreviewService } from '@shared/services/file-preview.service';
import { downloadBlobUtils } from '@src/app/core/utils/download-file.utils';
import { Observable, catchError, finalize, first, map, of, shareReplay, tap } from 'rxjs';
import { describeBriefingError } from './briefing-errors';

export const PDF_FALLBACK_MESSAGE =
  'Não foi possível baixar o PDF desta versão. Use Baixar PDF ou Abrir PDF de novo para tentar outra vez.';

/** Abrir PDF ou Exportar PDF. */
export type BriefingPdfAction = 'open' | 'download';

/** PDF de uma versão já baixado: o `objectUrl` serve ao leitor (Abrir PDF) e o blob ao Exportar PDF. */
export interface CachedPdf {
  blob: Blob;
  objectUrl: string;
}

/**
 * PDF oficial das versões do Briefing (card 3878): baixa, guarda e entrega para Abrir PDF e Exportar PDF.
 *
 * - Baixa uma vez por versão e guarda; a outra ação reaproveita o cache, e as duas ao mesmo tempo dividem o mesmo
 *   download. A tela não mostra o PDF embutido: a visualização é o HTML (Q3, `BriefingPreviewStore`).
 * - Falha vira mensagem por versão, mostrada no toast.
 * - `reset()` na troca de deal e ao sair da aba revoga os object URLs; download que chegar depois é descartado.
 *
 * Provido pela aba (`providers`), então o ciclo de vida acompanha a tela.
 */
@Injectable()
export class BriefingPdfStore implements OnDestroy {
  private readonly partnersHttp = inject(PartnersHttpService);
  private readonly toast = inject(ToastHelperService);
  private readonly filePreview = inject(FilePreviewService);
  private readonly destroyRef = inject(DestroyRef);

  /** Abrir PDF ou Exportar PDF em andamento (os botões mostram o carregamento). */
  private readonly actionState = signal<BriefingPdfAction | null>(null);
  readonly action = this.actionState.asReadonly();

  private readonly cache = signal<Record<string, CachedPdf>>({});
  /** Mensagem da falha ao baixar o PDF, por versão. */
  private readonly errors = signal<Record<string, string>>({});
  /** Downloads em andamento, um por versão (compartilhado entre Abrir PDF e Exportar PDF). */
  private readonly requests = new Map<string, Observable<CachedPdf | null>>();
  /** Muda a cada troca de deal e ao fechar a aba: download antigo que chegar depois é descartado. */
  private generation = 0;
  private destroyed = false;

  /** Abrir PDF: no leitor de arquivos da plataforma ou, sem ele, numa aba nova. */
  open(dealId: string, version: PartnerDealBriefingVersionResponse, fileName: string): void {
    this.ensure(dealId, version, 'open').subscribe(pdf => {
      if (!pdf) return;
      if (!this.filePreview.open(fileName, pdf.objectUrl)) {
        window.open(pdf.objectUrl, '_blank', 'noopener,noreferrer');
      }
    });
  }

  /** Exportar PDF: baixa o arquivo com o nome `briefing-<número>-v<versão>.pdf`. */
  download(dealId: string, version: PartnerDealBriefingVersionResponse, fileName: string): void {
    this.ensure(dealId, version, 'download').subscribe(pdf => {
      if (pdf) downloadBlobUtils(pdf.blob, fileName);
    });
  }

  /**
   * Solta os PDFs do deal: revoga os object URLs e esquece cache, erros e downloads em andamento. Na troca de deal
   * e ao fechar a aba (M1 e M2); download que ainda chegar depois disso é descartado pelo `generation`.
   */
  reset(): void {
    Object.values(this.cache()).forEach(pdf => URL.revokeObjectURL(pdf.objectUrl));
    this.generation++;
    this.requests.clear();
    this.cache.set({});
    this.errors.set({});
    this.actionState.set(null);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.reset();
  }

  /** O PDF da versão, do cache ou baixado agora. Com outra ação em andamento, não faz nada (`null`). */
  private ensure(
    dealId: string,
    version: PartnerDealBriefingVersionResponse,
    action: BriefingPdfAction
  ): Observable<CachedPdf | null> {
    const cached = this.cache()[version.id];
    if (cached) return of(cached);
    if (this.actionState()) return of(null);

    this.actionState.set(action);
    this.clearError(version.id);
    return this.fetch(dealId, version.id).pipe(
      tap(pdf => {
        if (!pdf) {
          this.toast.showToastError(
            'PDF indisponível',
            this.errors()[version.id] ?? PDF_FALLBACK_MESSAGE
          );
        }
      }),
      finalize(() => this.actionState.set(null)),
      takeUntilDestroyed(this.destroyRef)
    );
  }

  /** O download da versão, compartilhado entre quem pedir enquanto ele corre. Falha vira mensagem por versão. */
  private fetch(dealId: string, versionId: string): Observable<CachedPdf | null> {
    const pending = this.requests.get(versionId);
    if (pending) return pending;

    const generation = this.generation;
    const isStale = () => this.destroyed || generation !== this.generation;
    const request: Observable<CachedPdf | null> = this.partnersHttp
      .getDealBriefingPdf(dealId, versionId)
      .pipe(
        first(),
        map(blob => {
          // Aba fechada ou deal trocado no meio do download: não cria object URL que ninguém revogaria.
          if (isStale()) return null;
          if (!blob) {
            this.setError(versionId, PDF_FALLBACK_MESSAGE);
            return null;
          }
          return this.store(versionId, blob);
        }),
        catchError(err => {
          if (!isStale())
            this.setError(versionId, describeBriefingError(err, PDF_FALLBACK_MESSAGE));
          return of(null);
        }),
        finalize(() => {
          if (this.requests.get(versionId) === request) this.requests.delete(versionId);
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    this.requests.set(versionId, request);
    return request;
  }

  /**
   * Guarda o PDF. O blob é refeito com o tipo `application/pdf` fixo: o object URL tem a origem do app, e uma resposta
   * 200 com HTML (proxy, erro de infraestrutura) não pode virar página executável no leitor de PDF (M3).
   */
  private store(versionId: string, blob: Blob): CachedPdf {
    const pdfBlob = new Blob([blob], { type: 'application/pdf' });
    const objectUrl = URL.createObjectURL(pdfBlob);
    const pdf: CachedPdf = { blob: pdfBlob, objectUrl };
    this.cache.update(cache => ({ ...cache, [versionId]: pdf }));
    return pdf;
  }

  private setError(versionId: string, message: string): void {
    this.errors.update(errors => ({ ...errors, [versionId]: message }));
  }

  private clearError(versionId: string): void {
    if (!this.errors()[versionId]) return;
    this.errors.update(errors => {
      const next = { ...errors };
      delete next[versionId];
      return next;
    });
  }
}
